/**
 * The product tour — a spotlight and a card, walked through `TOUR_STEPS`.
 *
 * ## Fixed, and that is not a preference
 *
 * Every anchored surface in this app is `position: fixed`, and `GroupBar` says
 * why in its own header: the bar sets `overflow: hidden`, which is load-bearing
 * for the chip-fill measurement. Anything absolutely positioned inside it is
 * clipped. A tour that points at the group bar has to escape that, so the whole
 * overlay lives at the document root in fixed coordinates — which is also what
 * `getBoundingClientRect` already returns, so no scroll offset arithmetic is
 * needed anywhere.
 *
 * ## One dim layer, with holes cut in it
 *
 * This was `box-shadow: 0 0 0 9999px` on the highlight rect, which dims
 * everything outside it in a single paint — elegant, and capable of exactly
 * one hole. A step that lights two controls sitting apart (the interval box at
 * the top of the chart, the span presets at its foot) needs two, and a ring
 * stretched between them would enclose the whole chart and light nothing.
 *
 * So the ground is one masked `<svg>` rect: white where it dims, a black
 * rounded rect per hole. Still one paint, no four-rect scrim to keep in sync,
 * and no seams where two holes meet.
 *
 * ## The backdrop swallows clicks
 *
 * A tour that lets you click the thing it is describing sounds friendlier than
 * it is: the layout panel opens over the card, the card's "Next" moves to a
 * control that is now behind a popover, and the spotlight is measuring an
 * element that just moved. One blocker beneath the card, and the only ways out
 * are the tour's own buttons or Escape.
 *
 * ## It re-measures rather than remembering
 *
 * Positions come from `getBoundingClientRect` on every step change, resize and
 * scroll. The alternative — measuring once at open — drifts the moment anything
 * reflows, and this app reflows on a 2-second screener push.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { guideHref } from "../lib/guide";
import { TOUR_STEPS, markTourSeen, resolve, resolveSelector, type TourStep } from "../lib/tour";

export interface TourProps {
  /** Close it. The host owns whether the tour is mounted at all. */
  onClose: () => void;
}

/** Gap between the spotlight and the card, and the ring's breathing room. */
const PAD = 6;
const GAP = 12;
const CARD_WIDTH = 330;

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function Tour({ onClose }: TourProps) {
  /**
   * The steps this run will walk.
   *
   * Resolved **once, at open**, rather than filtered on every render: a step
   * whose target disappears mid-tour would otherwise renumber the ones after
   * it, so "3 of 9" would count down while you were reading it. A target that
   * vanishes is handled where it matters instead — `boxes` comes back empty and
   * the step is stepped over on the way past.
   */
  const steps = useMemo(() => TOUR_STEPS.filter((s) => resolve(s) !== null), []);
  const [index, setIndex] = useState(0);
  /**
   * One box per control this step lights — usually one, two when a step names
   * controls that sit apart. The interval box is at the top of the chart and
   * the span presets at the bottom; a single ring round both would enclose the
   * whole chart and light nothing.
   */
  const [boxes, setBoxes] = useState<Box[]>([]);
  const cardRef = useRef<HTMLDivElement | null>(null);
  /** Whatever had focus when the tour opened, so it can be handed back. */
  const returnFocus = useRef<HTMLElement | null>(null);

  const step: TourStep | undefined = steps[index];

  /**
   * Measure what this step is about, in viewport coordinates.
   *
   * **One box per control**, not a union of them: a step naming two controls
   * that sit apart gets two rings, where one rect round both would enclose
   * everything between. A step that opened a surface is measured on the
   * surface rather than on the button that revealed it.
   */
  const measure = useCallback(() => {
    if (!step) return;
    const rects: DOMRect[] = [];
    // A revealed surface replaces the anchor rather than joining it — the point
    // of the step is the panel, and a ring stretched from the button to the
    // foot of the panel would enclose most of the screen.
    const opened = step.reveal ? resolveSelector(step.reveal) : null;
    const primary = opened ?? resolve(step);
    if (primary) rects.push(primary.getBoundingClientRect());
    for (const selector of step.also ?? []) {
      const extra = resolveSelector(selector);
      if (extra) rects.push(extra.getBoundingClientRect());
    }
    setBoxes(
      rects.map((r) => ({
        top: r.top - PAD,
        left: r.left - PAD,
        width: r.width + PAD * 2,
        height: r.height + PAD * 2,
      })),
    );
  }, [step]);

  /**
   * Open what this step is about, and close it again on the way out.
   *
   * Openness is read from the surface, not from a flag of our own: clicking
   * blindly would close a panel the reader had already opened, and tidying up
   * blindly would re-open one that a stray click had dismissed — the layout
   * panel closes on any outside `pointerdown`, which every click during the
   * tour is.
   *
   * The re-measures are because the panel positions itself from its own first
   * measurement, so for a frame it sits at 0,0 with `visibility: hidden` — and
   * a rect at the top-left corner is exactly what the spotlight must not draw.
   */
  useEffect(() => {
    if (!step?.reveal) return;
    // **Ask whether the surface is on screen, not what the button claims.**
    // `aria-expanded` would do for the popovers, but the strip's keys use
    // `aria-pressed` for *active* rather than *open* — the conditions key is
    // pressed whenever a condition is applied, popover shut — so reading the
    // button would leave that step never opening and then toggling on the way
    // out. The surface's own presence cannot be wrong about this.
    const reveal = step.reveal;
    const isOpen = () => resolveSelector(reveal) !== null;
    const anchor = resolve(step);
    if (anchor && !isOpen()) anchor.click();
    const timers = [60, 200, 400].map((ms) => window.setTimeout(measure, ms));
    return () => {
      timers.forEach((t) => window.clearTimeout(t));
      const still = resolve(step);
      if (still && isOpen()) still.click();
    };
  }, [step, measure]);

  // Layout effect, not effect: measuring after paint shows the card at the
  // previous step's position for a frame, which reads as a jump.
  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    // `true` for scroll: a control inside a scrolling pane does not bubble its
    // scroll event to window, and the group bar and the grid both scroll.
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [measure]);

  /**
   * Keep focus inside the card.
   *
   * `aria-modal` is a promise to a screen reader and nothing else — it does not
   * contain Tab. Without this, one Tab from the Next button reaches the layout
   * tabs *behind* the scrim (the tour renders before `<LayoutBar>`), where Enter
   * switches layout: the anchor moves to a different button and the ring is left
   * on the old one. Two more Tabs open the layout panel underneath the overlay,
   * which is exactly what the blocker exists to prevent.
   *
   * The letter case is worse and less obvious. `lib/type-to-search.ts` lets a
   * focused `[role="dialog"]` keep its own letters; once focus escapes to
   * `document.body` that guard stops matching, so the next letter typed opens
   * the symbol dialog *under* the scrim with its search box focused — reachable
   * by neither pointer nor eye.
   *
   * `focusin` does not fire when focus falls to `body`, which is where a
   * backdrop click sends it, so the blocker refuses the blur as well.
   */
  useLayoutEffect(() => {
    // **Captured here, not in a passive effect, and there is no `autoFocus`.**
    // React applies `autoFocus` during commit — before passive effects flush —
    // so reading `activeElement` in a `useEffect` returns the tour's own
    // button and "restore what had focus" restores nothing. A layout effect
    // with no autoFocus above it sees the element the user actually left.
    const previous = document.activeElement;
    returnFocus.current = previous instanceof HTMLElement ? previous : null;
    cardRef.current?.querySelector<HTMLElement>("button, a[href]")?.focus();
  }, []);

  /**
   * Put focus back in the card whenever it leaves.
   *
   * `focusin` alone is not enough, and the gap is not exotic: **clicking Back
   * on step 2 removes the focused button**, and removing a focused element
   * sends focus to `document.body` without firing any focus event at all. So
   * the step effect below re-asserts, `mousedown` on the card's own text is
   * refused, and Back is always rendered — merely disabled on step 0 — so it
   * cannot be unmounted under the pointer.
   *
   * Body is the state that matters, because `lib/type-to-search.ts` decides by
   * asking whether the focused element is inside a `[role="dialog"]`. Body is
   * the tour root's *ancestor*, so that test can never pass from there, and the
   * next letter typed opens the symbol dialog underneath the scrim.
   */
  useEffect(() => {
    const pullBack = () => {
      const card = cardRef.current;
      if (!card || card.contains(document.activeElement)) return;
      card.querySelector<HTMLElement>("button, a[href]")?.focus();
    };
    const onFocusIn = (ev: FocusEvent) => {
      const card = cardRef.current;
      if (!card || (ev.target instanceof Node && card.contains(ev.target))) return;
      pullBack();
    };
    document.addEventListener("focusin", onFocusIn, true);
    // The body case fires nothing, so it is caught on the way back in instead.
    window.addEventListener("focus", pullBack);
    return () => {
      document.removeEventListener("focusin", onFocusIn, true);
      window.removeEventListener("focus", pullBack);
    };
  }, []);

  // Re-assert after every step, which is when a button can vanish under the
  // pointer and take focus to body with it.
  useEffect(() => {
    const card = cardRef.current;
    if (card && !card.contains(document.activeElement)) {
      card.querySelector<HTMLElement>("button, a[href]")?.focus();
    }
  }, [index]);

  const finish = useCallback(() => {
    // Marked on skip as well as on finish: both are the person answering the
    // question "have you been shown around", and re-offering it to somebody who
    // said no is the behaviour that makes people distrust a product.
    markTourSeen();
    // Hand focus back to whatever had it, so a keyboard user is not dropped at
    // the top of the document.
    returnFocus.current?.focus?.();
    onClose();
  }, [onClose]);

  const next = useCallback(() => {
    setIndex((i) => {
      // Step over anything that has gone missing since the tour opened rather
      // than showing a card with no spotlight.
      for (let n = i + 1; n < steps.length; n++) {
        const candidate = steps[n];
        if (candidate && resolve(candidate)) return n;
      }
      return steps.length; // past the end — the effect below closes it
    });
  }, [steps]);

  const back = useCallback(() => {
    setIndex((i) => {
      for (let n = i - 1; n >= 0; n--) {
        const candidate = steps[n];
        if (candidate && resolve(candidate)) return n;
      }
      return i;
    });
  }, [steps]);

  // Walking off the end finishes. Done as an effect so `next` stays a pure
  // index move and there is exactly one place the tour completes.
  useEffect(() => {
    if (steps.length === 0 || index >= steps.length) finish();
  }, [index, steps.length, finish]);

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      // Modified arrows are the browser's and the caret's — Alt+← is Back,
      // Ctrl/Option+← is word-wise. Stepping a tour on those, in the capture
      // phase, takes them away from everything downstream.
      if (ev.ctrlKey || ev.metaKey || ev.altKey || ev.shiftKey || ev.isComposing) return;
      if (ev.key === "Escape") {
        ev.preventDefault();
        finish();
      } else if (ev.key === "ArrowRight") {
        ev.preventDefault();
        next();
      } else if (ev.key === "ArrowLeft") {
        ev.preventDefault();
        back();
      }
    };
    // Capture, so a focused input inside the app cannot eat the arrow keys.
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [finish, next, back]);

  if (!step || index >= steps.length) return null;

  // Placed against the union, so the card clears every ring rather than only
  // the first — the rings themselves stay separate.
  const card = cardPosition(union(boxes));

  return (
    <div style={ROOT} role="dialog" aria-modal="true" aria-label="Product tour">
      {/* Swallows every click that is not the card's — and does **not** close.
          A backdrop that dismisses is the usual pattern and is wrong here,
          because ending the tour also records it as seen: one stray click and
          somebody has silently used up the only time they were going to be
          shown around. Skip says the same thing on purpose, and Escape is
          there for anyone who expects a backdrop to work. */}
      <div style={BLOCKER} onMouseDown={(ev) => ev.preventDefault()} />

      {/* **The dim is one layer with holes cut in it, not a shadow.**
          `box-shadow: 0 0 0 9999px` makes exactly one hole, which was fine
          while a step lit one control. A mask takes as many as the step needs
          and still paints in a single pass, with no seams where holes meet. */}
      {boxes.length > 0 && (
        <svg style={SCRIM} aria-hidden="true">
          <defs>
            <mask id="qf-tour-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {boxes.map((b, i) => (
                <rect
                  key={i}
                  x={b.left}
                  y={b.top}
                  width={b.width}
                  height={b.height}
                  rx="7"
                  fill="black"
                />
              ))}
            </mask>
          </defs>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="rgba(6, 10, 18, 0.62)"
            mask="url(#qf-tour-mask)"
          />
        </svg>
      )}
      {boxes.map((b, i) => (
        <div
          key={i}
          style={{ ...SPOT, top: b.top, left: b.left, width: b.width, height: b.height }}
        />
      ))}

      <div
        ref={cardRef}
        style={{ ...CARD, top: card.top, left: card.left }}
        // Refuse the blur from a mousedown on the card's *text* — the counter,
        // the heading, the padding — which finds no focusable ancestor and so
        // clears focus to body. A press on a real control is left alone, or it
        // could never take focus in the first place.
        onMouseDown={(ev) => {
          if (!(ev.target instanceof Element) || !ev.target.closest("button, a[href]")) {
            ev.preventDefault();
          }
        }}
      >
        <div style={COUNT}>
          Step {index + 1} of {steps.length}
        </div>
        <h3 style={TITLE}>{step.title}</h3>
        <p style={BODY}>{step.body}</p>
        <div style={ROW}>
          <button type="button" style={QUIET} onClick={finish}>
            Skip
          </button>
          {step.section && (
            <a
              href={guideHref(step.section)}
              target="_blank"
              rel="noopener noreferrer"
              style={{ ...QUIET, textDecoration: "none" }}
            >
              Read more
            </a>
          )}
          <span style={{ flex: "1 1 auto" }} />
          {/* **Always rendered, disabled on the first step** rather than
              conditional. Un-rendering the button the pointer just activated
              is what dropped focus to `document.body` — see the effect above. */}
          <button type="button" style={QUIET} onClick={back} disabled={index === 0}>
            Back
          </button>
          <button type="button" style={PRIMARY} onClick={next}>
            {index === steps.length - 1 ? "Done" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** The one rect enclosing them all, or null when there are none. */
function union(boxes: readonly Box[]): Box | null {
  if (boxes.length === 0) return null;
  const top = Math.min(...boxes.map((b) => b.top));
  const left = Math.min(...boxes.map((b) => b.left));
  const right = Math.max(...boxes.map((b) => b.left + b.width));
  const bottom = Math.max(...boxes.map((b) => b.top + b.height));
  return { top, left, width: right - left, height: bottom - top };
}

/**
 * Where the card goes: under the target, or above it when there is no room.
 *
 * Clamped to the viewport on both axes, because a spotlight near an edge would
 * otherwise push the card half off screen — the group bar sits at the top right
 * and the star column at the far left, so both edges are real cases here.
 */
function cardPosition(box: Box | null): { top: number; left: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // Tall enough for the longest body at this width, and only used to decide
  // which side of the target to sit on — the card itself is content-sized.
  const assumed = 190;

  if (!box) {
    return { top: Math.max(GAP, vh / 2 - assumed / 2), left: Math.max(GAP, vw / 2 - CARD_WIDTH / 2) };
  }

  // **A tall target is sat beside, not under.** The layout panel is up to 70vh,
  // so there is no room above or below it — stacking would push the card off
  // screen, and clamping it back would drop it on top of the thing it is
  // describing. Sit on whichever side has more room, aligned with the top.
  if (box.height > vh * 0.45) {
    const roomLeft = box.left;
    const roomRight = vw - (box.left + box.width);
    const left =
      roomRight >= roomLeft
        ? Math.min(box.left + box.width + GAP, Math.max(GAP, vw - CARD_WIDTH - GAP))
        : Math.max(GAP, box.left - GAP - CARD_WIDTH);
    const top = Math.min(Math.max(GAP, box.top), Math.max(GAP, vh - assumed - GAP));
    return { top, left };
  }

  const below = box.top + box.height + GAP;
  const above = box.top - GAP - assumed;
  const top = below + assumed <= vh - GAP ? below : Math.max(GAP, above);

  const wanted = box.left + box.width / 2 - CARD_WIDTH / 2;
  const left = Math.min(Math.max(GAP, wanted), Math.max(GAP, vw - CARD_WIDTH - GAP));

  return { top, left };
}

const ROOT: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 9000,
  pointerEvents: "none",
};

const BLOCKER: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  pointerEvents: "auto",
};

/** The dimmed ground, with this step's holes cut out of it. */
const SCRIM: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  width: "100%",
  height: "100%",
  pointerEvents: "none",
};

/** The accent ring round one hole. The dimming is the scrim's job now. */
const SPOT: React.CSSProperties = {
  position: "fixed",
  borderRadius: 7,
  border: "2px solid var(--qf-accent)",
  pointerEvents: "none",
  transition: "top .16s ease, left .16s ease, width .16s ease, height .16s ease",
};

const CARD: React.CSSProperties = {
  position: "fixed",
  width: CARD_WIDTH,
  maxWidth: "calc(100vw - 24px)",
  pointerEvents: "auto",
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  borderRadius: 9,
  padding: "13px 15px 12px",
  boxShadow: "0 10px 34px rgba(0,0,0,.42)",
  color: "var(--qf-text)",
};

const COUNT: React.CSSProperties = {
  fontSize: "0.68rem",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--qf-text-secondary)",
};

const TITLE: React.CSSProperties = {
  margin: "5px 0 6px",
  fontSize: "0.98rem",
  fontWeight: 650,
};

const BODY: React.CSSProperties = {
  margin: "0 0 12px",
  fontSize: "0.82rem",
  lineHeight: 1.5,
  color: "var(--qf-text-secondary)",
};

const ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 7,
};

const QUIET: React.CSSProperties = {
  padding: "0.28rem 0.6rem",
  background: "transparent",
  border: "1px solid var(--qf-border)",
  borderRadius: 5,
  color: "var(--qf-text-secondary)",
  font: "inherit",
  fontSize: "0.76rem",
  cursor: "pointer",
};

const PRIMARY: React.CSSProperties = {
  ...QUIET,
  background: "var(--qf-accent)",
  borderColor: "var(--qf-accent)",
  color: "#fff",
  fontWeight: 600,
};
